import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Alert
} from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { format, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, isSameMonth, getDay, startOfDay } from 'date-fns';
import { lt } from 'date-fns/locale';
import {
  updateAvailableSlots,
  getAvailableSlotsInRange,
  setCoachRecurringRange,
  getWeekDates,
  generateTimeSlots,
  coachToggleSlot,
  getSlotVenueBackground,
  normalizeSlot,
  createDefaultSlotObject,
  ALL_TRAINING_VENUE_IDS,
  TRAINING_VENUES,
  getCoachByUid,
  getCoachById,
  DEFAULT_COACH_ID
} from '../utils/firebaseUtils';
import VenueFilterCheckboxes from './VenueFilterCheckboxes';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { isUserAdmin } from '../utils/adminUtils';

const WEEKDAY_LETTERS = ['Pr', 'An', 'Tr', 'Kt', 'Pn', 'Št', 'Sk'];
const WEEKDAY_NAMES = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];

const CoachNuolatiniaiLaikaiPage = () => {
  const navigate = useNavigate();
  const { currentUser, approvedCoachUids } = useAuth();
  const currentCoach = currentUser ? getCoachByUid(currentUser.uid) : null;
  const hasCoachPanel = currentCoach != null || (approvedCoachUids && approvedCoachUids.includes(currentUser?.uid));
  const currentCoachId = currentCoach?.id ?? (approvedCoachUids?.includes(currentUser?.uid) ? currentUser.uid : DEFAULT_COACH_ID);

  const today = new Date();
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [displayMonth, setDisplayMonth] = useState(() => startOfMonth(today));

  const validFrom = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeStart : rangeEnd) : null;
  const validTo = rangeStart && rangeEnd ? (rangeStart <= rangeEnd ? rangeEnd : rangeStart) : null;
  const rangeStep = !rangeStart && !rangeEnd ? 0 : rangeStart && rangeEnd ? 2 : 1;

  const clearRange = () => {
    setRangeStart(null);
    setRangeEnd(null);
  };

  const referenceWeek = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekDates = getWeekDates(format(referenceWeek, 'yyyy-MM-dd'));
  const [availableSlots, setAvailableSlots] = useState({});
  const [error, setError] = useState('');
  const [hasExistingInRange, setHasExistingInRange] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedVenueIds, setSelectedVenueIds] = useState(ALL_TRAINING_VENUE_IDS);

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    if (!hasCoachPanel || isUserAdmin(currentUser)) {
      navigate('/admin', { replace: true });
      return;
    }
  }, [hasCoachPanel, currentUser, navigate]);

  const getSlotCellStyle = (date, time) => {
    const slot = availableSlots[date]?.timeSlots?.find((s) => s.time === time);
    const bg = getSlotVenueBackground(slot, ALL_TRAINING_VENUE_IDS);
    const isGradient = typeof bg === 'string' && bg.includes('gradient');
    return { background: bg, backgroundColor: isGradient ? undefined : bg };
  };

  const handleTimeSlotClick = (date, time) => {
    if (selectedVenueIds.length === 0) {
      setError('Pasirinkite bent vieną vietą');
      return;
    }
    let currentSlots = availableSlots[date]?.timeSlots;
    if (!currentSlots) {
      currentSlots = timeSlots.map((timeSlot) => createDefaultSlotObject(timeSlot));
    }
    const currentSlot = currentSlots.find((s) => s.time === time);
    const toggled = coachToggleSlot(currentSlot || { time }, selectedVenueIds);
    const updatedTimeSlots = currentSlots.map((slot) =>
      slot.time === time ? toggled : normalizeSlot(slot)
    );
    setAvailableSlots((prev) => ({
      ...prev,
      [date]: { date, timeSlots: updatedTimeSlots }
    }));
    setError('');
  };

  const getTemplateTimeSlotsForWeekday = (weekdayIndex) => {
    const dateKey = weekDates[weekdayIndex];
    const dayData = availableSlots[dateKey];
    if (dayData?.timeSlots?.length) return dayData.timeSlots.map(normalizeSlot);
    return timeSlots.map((t) => createDefaultSlotObject(t));
  };

  useEffect(() => {
    if (!hasCoachPanel || isUserAdmin(currentUser) || !validFrom || !validTo) return;
    const fromStr = format(validFrom, 'yyyy-MM-dd');
    const toStr = format(validTo, 'yyyy-MM-dd');
    getAvailableSlotsInRange(fromStr, toStr, currentCoachId).then((slots) => {
      setHasExistingInRange(Object.keys(slots).length > 0);
    });
  }, [validFrom, validTo, currentCoachId, hasCoachPanel, currentUser]);

  const handleSubmit = async () => {
    if (!validFrom || !validTo) return;
    setSubmitLoading(true);
    setError('');
    setSubmitSuccess(false);
    try {
      const fromStr = format(validFrom, 'yyyy-MM-dd');
      const toStr = format(validTo, 'yyyy-MM-dd');
      const days = eachDayOfInterval({ start: validFrom, end: validTo });
      const writes = days.map((d) => {
        const dateStr = format(d, 'yyyy-MM-dd');
        const weekdayIndex = (getDay(d) + 6) % 7;
        const templateTimeSlots = getTemplateTimeSlotsForWeekday(weekdayIndex);
        return updateAvailableSlots(dateStr, templateTimeSlots, currentCoachId);
      });
      await Promise.all(writes);
      await setCoachRecurringRange(
        currentCoachId,
        getCoachById(currentCoachId)?.name ?? 'Treneris',
        fromStr,
        toStr
      );
      setHasExistingInRange(true);
      setSubmitSuccess(true);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Klaida išsaugant.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleRangeDayClick = (day) => {
    const hasStart = !!rangeStart;
    const hasEnd = !!rangeEnd;
    if (!hasStart && !hasEnd) {
      setRangeStart(startOfDay(day));
      setRangeEnd(null);
    } else if (hasStart && !hasEnd) {
      const start = rangeStart;
      const d = startOfDay(day);
      if (d < start) {
        setRangeStart(d);
        setRangeEnd(start);
      } else {
        setRangeEnd(d);
      }
    } else {
      clearRange();
    }
  };

  const renderRangeCalendar = () => {
    const monthStart = startOfMonth(displayMonth);
    const monthEnd = endOfMonth(displayMonth);
    const startPad = (monthStart.getDay() + 6) % 7;
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const leadingEmpty = Array(startPad).fill(null);
    const allCells = [...leadingEmpty, ...daysInMonth];
    const from = validFrom ? startOfDay(validFrom) : null;
    const to = validTo ? startOfDay(validTo) : null;
    const dayStart = (d) => startOfDay(d);
    const inRange = (d) => {
      if (rangeStep === 1 && rangeStart) return dayStart(d).getTime() === startOfDay(rangeStart).getTime();
      return from && to && dayStart(d) >= from && dayStart(d) <= to;
    };
    const isRangeEdge = (d) => {
      const t = dayStart(d).getTime();
      if (rangeStep === 1 && rangeStart) return t === startOfDay(rangeStart).getTime();
      if (!from || !to) return false;
      return t === from.getTime() || t === to.getTime();
    };
    return (
      <Paper sx={{ p: 2, minWidth: 200, maxWidth: 280 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Galiojimo laikotarpis
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <IconButton size="small" onClick={() => setDisplayMonth((m) => subMonths(m, 1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {format(displayMonth, 'MMMM yyyy', { locale: lt })}
          </Typography>
          <IconButton size="small" onClick={() => setDisplayMonth((m) => addMonths(m, 1))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {WEEKDAY_LETTERS.map((d) => (
            <Box key={d} sx={{ width: 'calc(14.28% - 4px)', textAlign: 'center', fontSize: '0.7rem', color: 'text.secondary' }}>
              {d}
            </Box>
          ))}
          {allCells.map((day, i) => {
            if (!day) {
              return <Box key={`e-${i}`} sx={{ width: 'calc(14.28% - 4px)', height: 32 }} />;
            }
            const inRangeGreen = inRange(day);
            const isEdge = isRangeEdge(day);
            const isCurrentMonth = isSameMonth(day, displayMonth);
            return (
              <Button
                key={day.toISOString()}
                size="small"
                onClick={() => handleRangeDayClick(day)}
                sx={{
                  minWidth: 0,
                  width: 'calc(14.28% - 4px)',
                  height: 32,
                  p: 0,
                  fontSize: '0.75rem',
                  bgcolor: inRangeGreen ? (isEdge ? 'success.dark' : 'success.main') : 'transparent',
                  color: inRangeGreen ? 'success.contrastText' : isCurrentMonth ? 'text.primary' : 'text.disabled',
                  outline: isEdge && inRangeGreen ? '2px solid' : 'none',
                  outlineColor: 'success.contrastText',
                  '&:hover': { bgcolor: inRangeGreen ? 'success.dark' : 'action.hover' }
                }}
              >
                {format(day, 'd')}
              </Button>
            );
          })}
        </Box>
      </Paper>
    );
  };

  if (!hasCoachPanel || isUserAdmin(currentUser)) {
    return null;
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" sx={{ mb: 4, textAlign: 'center', fontWeight: 600 }}>
        Nuolatiniai laisvi laikai
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
        Pasirinkite, nuo kurios iki kurios datos galioja žemiau nustatyti laisvi laikai. Nepamirškite paspausti Pateikti/Atnaujinti mygtuko.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
        {renderRangeCalendar()}
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {validFrom && validTo
            ? <>Galioja nuo: <strong>{format(validFrom, 'yyyy-MM-dd', { locale: lt })}</strong> — Galioja iki: <strong>{format(validTo, 'yyyy-MM-dd', { locale: lt })}</strong></>
            : 'Pasirinkite datų intervalą kalendoriuje.'}
        </Typography>
        {validFrom && validTo && (
          <Button size="small" color="inherit" onClick={clearRange} sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}>
            Išvalyti
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4, gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={submitLoading || !validFrom || !validTo}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {submitLoading ? 'Siunčiama...' : hasExistingInRange ? 'Atnaujinti' : 'Pateikti'}
        </Button>
        {submitSuccess && (
          <Typography variant="body2" color="success.main" fontWeight={500}>
            Išsaugota.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1.5, gap: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => { setAvailableSlots({}); setError(''); }}
          sx={{ textTransform: 'none' }}
        >
          Išvalyti savaitės šabloną
        </Button>
      </Box>

      <VenueFilterCheckboxes
        selectedVenueIds={selectedVenueIds}
        onChange={setSelectedVenueIds}
        label="Redaguojamos vietos"
      />

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 0.25, sm: 2, md: 3 }, pl: { xs: 0.5, sm: 3, md: 4 }, maxWidth: 1000, width: '100%', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', minWidth: { xs: '100%', sm: 'max-content' } }}>
            <Box sx={{ position: 'sticky', left: 0, zIndex: 5, bgcolor: 'background.paper', borderRight: '1px solid', borderColor: 'divider', minWidth: { xs: 22, sm: 50, md: 60 }, flexShrink: 0 }}>
              <Box sx={{ height: { xs: 32, sm: 55, md: 60 }, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.55rem', sm: '0.875rem', md: '1rem' } }}>Laikas</Typography>
              </Box>
              {timeSlots.map((time, index) => {
                const isHalfHour = time.includes(':30');
                const isHourEnd = (index + 1) % 2 === 0;
                return (
                  <Box
                    key={time}
                    sx={{
                      height: { xs: 18, sm: 28, md: 30 },
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      pt: 0.25,
                      fontSize: { xs: '0.55rem', sm: '0.75rem', md: '0.8rem' },
                      color: 'text.secondary',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0
                    }}
                  >
                    {!isHalfHour ? time : ''}
                  </Box>
                );
              })}
            </Box>
            {weekDates.map((date, dayIndex) => (
              <Box key={date} sx={{ minWidth: { xs: 0, sm: 100, md: 120 }, flex: { xs: 1, sm: 'none' }, borderRight: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ height: { xs: 32, sm: 55, md: 60 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.5rem', sm: '0.8rem' }, textAlign: 'center' }}>
                    {WEEKDAY_NAMES[dayIndex]}
                  </Typography>
                </Box>
                {timeSlots.map((time, index) => {
                  const cellStyle = getSlotCellStyle(date, time);
                  const isHourEnd = (index + 1) % 2 === 0;
                  return (
                    <Box
                      key={`${date}-${time}`}
                      onClick={() => handleTimeSlotClick(date, time)}
                      sx={{
                        height: { xs: 18, sm: 28, md: 30 },
                        background: cellStyle.background,
                        backgroundColor: cellStyle.backgroundColor || 'grey.300',
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: isHourEnd && index < timeSlots.length - 1 ? 0.25 : 0
                      }}
                    />
                  );
                })}
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        {TRAINING_VENUES.map((venue) => (
          <Box key={venue.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: venue.color, borderRadius: 1 }} />
            <Typography variant="body2">Laisvas laikas {venue.label}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: 'grey.300', borderRadius: 1 }} />
          <Typography variant="body2">Nepasiekiama</Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default CoachNuolatiniaiLaikaiPage;
